import React, { useEffect, useState } from 'react';
import { CheckCircle, Plus, XCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { leaveAPI, leaveTypesAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const defaultLeaveTypes = [];

const tabs = [
  { id: 'requests', label: 'Leave Requests' },
  { id: 'types', label: 'Leave Types' },
  { id: 'history', label: 'Leave History' },
];

const LeaveManagement = () => {
  const { toast } = useToast();
  const { user, hasPermission, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [leaveTypes, setLeaveTypes] = useState(defaultLeaveTypes);
  const [newType, setNewType] = useState({ code: '', label: '', description: '' });
  const [applications, setApplications] = useState([]);
  const [balance, setBalance] = useState(null);
  const [requestForm, setRequestForm] = useState({ leave_type: 'sick', start_date: '', end_date: '', days: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [rejectionDialog, setRejectionDialog] = useState({ open: false, application: null, reason: '' });
  const canApproveLeave = hasRole('super_admin') || hasPermission('leave:request:approve') || hasPermission('leave:approve');

  useEffect(() => {
    refreshLeaveData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadLeaveTypes();
  }, []);

  const loadLeaveTypes = async () => {
    try {
      const data = await leaveTypesAPI.getLeaveTypes();
      const types = Array.isArray(data) ? data : data || [];
      setLeaveTypes(types);
    } catch (error) {
      console.error('Failed to load leave types', error);
      setLeaveTypes([]);
    }
  };

  const refreshLeaveData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([loadBalance(), loadApplications()]);
      toast.success('Leave data synced successfully.');
    } catch (error) {
      toast.error('Unable to refresh leave data.', error.response?.data?.detail || 'Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const loadBalance = async () => {
    try {
      const data = await leaveAPI.getBalance();
      setBalance(data);
    } catch (error) {
      console.error('Failed to load leave balance', error);
      setBalance(null);
    }
  };

  const loadApplications = async () => {
    try {
      const data = await leaveAPI.getApplications();
      const apps = Array.isArray(data) ? data : data.applications || [];
      setApplications(apps);
    } catch (error) {
      console.error('Failed to load leave applications', error);
      setApplications([]);
    }
  };

  const handleTypeSubmit = async (event) => {
    event.preventDefault();
    const code = newType.code.trim().toLowerCase();
    const label = newType.label.trim();

    if (!code || !label) {
      toast.error('Leave type code and label are required.');
      return;
    }

    setLoading(true);
    try {
      await leaveTypesAPI.createLeaveType({ code, label, description: newType.description.trim() });
      setNewType({ code: '', label: '', description: '' });
      toast.success('Leave type saved.');
      await loadLeaveTypes();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save leave type.';
      toast.error('Failed to save leave type', message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveType = (typeId) => {
    // Removal not yet implemented on backend; remove locally for now
    setLeaveTypes(leaveTypes.filter((type) => type.id !== typeId));
    toast.success('Leave type removed locally.');
  };

  const handleRequestSubmit = async (event) => {
    event.preventDefault();
    const { leave_type, start_date, end_date, days, reason } = requestForm;
    const parsedDays = Number(days);

    if (!leave_type || !start_date || !end_date || !parsedDays || !reason.trim()) {
      toast.error('All fields are required to submit a leave request.');
      return;
    }

    setLoading(true);
    try {
      await leaveAPI.applyLeave({
        leave_type,
        start_date,
        end_date,
        days: parsedDays,
        reason: reason.trim(),
      });
      toast.success('Leave request submitted successfully.');
      setRequestForm({ leave_type, start_date: '', end_date: '', days: '', reason: '' });
      await loadApplications();
      await loadBalance();
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to submit leave request.';
      toast.error('Leave request failed.', message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLeave = async (application) => {
    setActionLoadingId(application.id);
    try {
      await leaveAPI.approveLeave(application.id, { status: 'approved' });
      toast.success('Leave request approved.');
      await loadApplications();
      await loadBalance();
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to approve leave request.';
      toast.error('Approval failed.', message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const openRejectDialog = (application) => {
    setRejectionDialog({ open: true, application, reason: '' });
  };

  const closeRejectDialog = () => {
    setRejectionDialog({ open: false, application: null, reason: '' });
  };

  const handleRejectLeave = async (event) => {
    event.preventDefault();
    const reason = rejectionDialog.reason.trim();
    const application = rejectionDialog.application;

    if (!application) return;
    if (!reason) {
      toast.error('Rejection reason is required.');
      return;
    }

    setActionLoadingId(application.id);
    try {
      await leaveAPI.approveLeave(application.id, { status: 'rejected', rejection_reason: reason });
      toast.success('Leave request rejected.');
      closeRejectDialog();
      await loadApplications();
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to reject leave request.';
      toast.error('Rejection failed.', message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const activeApplications = applications.filter((app) => app.status === 'pending');
  const historyApplications = applications.filter((app) => app.status !== 'pending');
  const canActOnApplication = (application) => {
    if (hasRole('super_admin')) return true;
    return canApproveLeave && application.manager_id === user?.id && application.user_id !== user?.id;
  };

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Leave Management</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Manage leave requests, review team leave history, and maintain leave type definitions.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={refreshLeaveData} disabled={refreshing}>
            <RefreshCw size={16} />
            <span className="ml-2">Refresh</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>{tabs.find((tab) => tab.id === activeTab)?.label}</CardTitle>
            <p className="mt-1 text-sm text-slate-600">
              Manage leave requests, review leave history, and maintain leave types in one place.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{applications.length} total applications</Badge>
            {balance && <Badge variant="outline">Balance loaded</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-full px-4 py-2 text-sm transition ${activeTab === tab.id ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'requests' && (
            <div className="space-y-6">
              <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
                <Card className="bg-slate-50">
                  <CardHeader>
                    <CardTitle>Leave Balance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {balance && balance.leave_counts && Array.isArray(balance.leave_counts) ? (
                      <div className="space-y-2">
                        {balance.leave_counts.map((item) => {
                          const remaining = item.total - item.used;
                          return (
                            <div key={item.id || item.leave_type_id} className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">{item.leave_type|| item.label}</span>
                              <span className="font-semibold text-slate-900">{remaining} days</span>
                            </div>
                          );
                        })}
                        <div className="text-xs text-slate-500 mt-3 pt-3 border-t">Fiscal year: {balance.fiscal_year || 'N/A'}</div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-600">No active leave balance available yet.</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Apply for Leave</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <form onSubmit={handleRequestSubmit} className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="leave_type">Leave Type</Label>
                          <Select
                            id="leave_type"
                            value={requestForm.leave_type}
                            onChange={(event) => setRequestForm({ ...requestForm, leave_type: event.target.value })}
                          >
                            {leaveTypes.map((type) => (
                              <option key={type.id} value={type.code}>{type.label}</option>
                            ))}
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="days">Days</Label>
                          <Input
                            id="days"
                            type="number"
                            min="1"
                            value={requestForm.days}
                            onChange={(event) => setRequestForm({ ...requestForm, days: event.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="start_date">Start Date</Label>
                          <Input
                            id="start_date"
                            type="date"
                            value={requestForm.start_date}
                            onChange={(event) => setRequestForm({ ...requestForm, start_date: event.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="end_date">End Date</Label>
                          <Input
                            id="end_date"
                            type="date"
                            value={requestForm.end_date}
                            onChange={(event) => setRequestForm({ ...requestForm, end_date: event.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="reason">Reason</Label>
                        <Textarea
                          id="reason"
                          rows={4}
                          value={requestForm.reason}
                          onChange={(event) => setRequestForm({ ...requestForm, reason: event.target.value })}
                          placeholder="Explain why you're requesting leave"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button type="submit" disabled={loading}>
                          <Plus size={16} />
                          <span className="ml-2">Submit Request</span>
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-slate-900">Active Leave Applications</h2>
                <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left">Employee</th>
                        <th className="px-4 py-3 text-left">Type</th>
                        <th className="px-4 py-3 text-left">Period</th>
                        <th className="px-4 py-3 text-left">Days</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        {canApproveLeave && <th className="px-4 py-3 text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {activeApplications.length ? activeApplications.map((application) => (
                        <tr key={application.id}>
                          <td className="px-4 py-3">{application.user_id}</td>
                          <td className="px-4 py-3">{application.leave_type}</td>
                          <td className="px-4 py-3">{application.start_date} → {application.end_date}</td>
                          <td className="px-4 py-3">{application.days}</td>
                          <td className="px-4 py-3">
                            <Badge variant="secondary">{application.status}</Badge>
                          </td>
                          {canApproveLeave && (
                            <td className="px-4 py-3">
                              {canActOnApplication(application) ? (
                                <div className="flex justify-end gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    disabled={actionLoadingId === application.id}
                                    onClick={() => handleApproveLeave(application)}
                                    className="text-green-700"
                                  >
                                    <CheckCircle size={14} />
                                    <span className="ml-1">Approve</span>
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="danger"
                                    disabled={actionLoadingId === application.id}
                                    onClick={() => openRejectDialog(application)}
                                  >
                                    <XCircle size={14} />
                                    <span className="ml-1">Reject</span>
                                  </Button>
                                </div>
                              ) : (
                                <span className="block text-right text-xs text-slate-400">Own request</span>
                              )}
                            </td>
                          )}
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={canApproveLeave ? 6 : 5} className="px-4 py-6 text-center text-sm text-slate-500">
                            No pending leave requests at the moment.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'types' && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                {leaveTypes.map((type) => (
                  <Card key={type.id}>
                    <CardHeader>
                      <CardTitle>{type.label}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-slate-600">{type.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">Code: {type.id}</Badge>
                        <Button variant="ghost" onClick={() => handleRemoveType(type.id)}>
                          <XCircle size={16} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Add New Leave Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleTypeSubmit} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div>
                        <Label htmlFor="code">Type Code</Label>
                        <Input
                          id="code"
                          value={newType.code}
                          onChange={(event) => setNewType({ ...newType, code: event.target.value })}
                          placeholder="sick"
                        />
                      </div>
                      <div>
                        <Label htmlFor="label">Label</Label>
                        <Input
                          id="label"
                          value={newType.label}
                          onChange={(event) => setNewType({ ...newType, label: event.target.value })}
                          placeholder="Sick Leave"
                        />
                      </div>
                      <div>
                        <Label htmlFor="description">Description</Label>
                        <Input
                          id="description"
                          value={newType.description}
                          onChange={(event) => setNewType({ ...newType, description: event.target.value })}
                          placeholder="Leave description"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button type="submit">
                        <Plus size={16} />
                        <span className="ml-2">Add Type</span>
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="px-4 py-3 text-left">Applicant</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Period</th>
                      <th className="px-4 py-3 text-left">Days</th>
                      <th className="px-4 py-3 text-left">Manager</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {historyApplications.length ? historyApplications.map((application) => (
                      <tr key={application.id}>
                        <td className="px-4 py-3">{application.user_id}</td>
                        <td className="px-4 py-3">{application.leave_type}</td>
                        <td className="px-4 py-3">{application.start_date} → {application.end_date}</td>
                        <td className="px-4 py-3">{application.days}</td>
                        <td className="px-4 py-3">{application.manager_id || '—'}</td>
                        <td className="px-4 py-3">
                          <Badge variant={application.status === 'approved' ? 'success' : application.status === 'rejected' ? 'error' : 'secondary'}>
                            {application.status}
                          </Badge>
                          {application.status === 'rejected' && application.rejection_reason && (
                            <p className="mt-1 max-w-xs text-xs text-slate-500">{application.rejection_reason}</p>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" className="px-4 py-6 text-center text-sm text-slate-500">
                          No leave history records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {rejectionDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Reject Leave Request</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Add the reason so the applicant can see why this request was rejected.
                </p>
              </div>
              <button type="button" onClick={closeRejectDialog} className="text-slate-400 hover:text-slate-600">
                <XCircle size={20} />
              </button>
            </div>
            <form onSubmit={handleRejectLeave} className="mt-4 space-y-4">
              <div>
                <Label htmlFor="rejection_reason">Rejection Reason</Label>
                <Textarea
                  id="rejection_reason"
                  rows={4}
                  value={rejectionDialog.reason}
                  onChange={(event) => setRejectionDialog({ ...rejectionDialog, reason: event.target.value })}
                  placeholder="Explain why this leave request is rejected"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={closeRejectDialog}>
                  Cancel
                </Button>
                <Button type="submit" variant="danger" loading={actionLoadingId === rejectionDialog.application?.id}>
                  Reject Request
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
